import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Grid2,
    Container,
    Button,
    Card,
    CardContent,
    Typography,
    Stack,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Alert,
    CircularProgress,
    Divider,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getAdvancedSalesReport } from '../../features/StockReports/salesReportSlice';
import Navbar from '../setup/Navbar';
import HeadingText from '../../BuiltInComponent/HeadingTest';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import PrintIcon from '@mui/icons-material/Print';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import axios from 'axios';
import { fetchPosOrderTypeSettings } from '../../utils/posOrderTypeSettings';

const defaultFilters = {
    scope: 'today',
    fromDate: '',
    toDate: '',
    selectedUserID: 'all',
    orderType: 'all',
    orderStatus: 'completed',
    paymentMethod: 'all',
    minAmount: '',
    maxAmount: '',
    search: '',
    itemSearch: ''
};

const THERMAL_PREVIEW_WIDTH = 42;

const parseThermalLine = (line) => {
    const raw = String(line ?? '');
    const text = raw.trim();
    if (!text) return { type: 'blank', text: '' };
    if (/^[=-]+$/.test(text)) return { type: 'divider', text };

    const pair = text.match(/^(.+?)\s{2,}(\S.*)$/);
    if (pair) return { type: 'row', left: pair[1].trim(), right: pair[2].trim() };

    const isCentered = raw !== text || /^([A-Z][A-Z\s:&/()-]+)$/.test(text);
    return { type: isCentered ? 'heading' : 'text', text };
};

const escapePrintHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const thermalClamp = (value, max = THERMAL_PREVIEW_WIDTH) => {
    const text = String(value ?? '');
    if (text.length <= max) return text;
    if (max <= 3) return text.slice(0, max);
    return `${text.slice(0, max - 3)}...`;
};

const thermalCenter = (value, width = THERMAL_PREVIEW_WIDTH) => {
    const text = thermalClamp(value, width);
    const totalPadding = Math.max(0, width - text.length);
    const left = Math.floor(totalPadding / 2);
    const right = totalPadding - left;
    return `${' '.repeat(left)}${text}${' '.repeat(right)}`;
};

const thermalLeftRight = (left, right, width = THERMAL_PREVIEW_WIDTH) => {
    const rightText = thermalClamp(String(right ?? ''), width);
    const maxLeftLength = Math.max(0, width - rightText.length - 1);
    const leftText = thermalClamp(String(left ?? ''), maxLeftLength || width);
    const spacing = width - leftText.length - rightText.length;

    if (spacing < 1) {
        return `${thermalClamp(leftText, width - rightText.length - 1)} ${rightText}`;
    }

    return `${leftText}${' '.repeat(spacing)}${rightText}`;
};

const thermalWrap = (value, width = THERMAL_PREVIEW_WIDTH) => {
    const text = String(value ?? '').replace(/\s+/g, ' ').trim();
    if (!text) return [];

    const words = text.split(' ');
    const lines = [];
    let current = '';

    words.forEach((word) => {
        if (!current) {
            current = word;
            return;
        }

        if ((current.length + 1 + word.length) <= width) {
            current += ` ${word}`;
        } else {
            lines.push(current);
            current = word;
        }
    });

    if (current) lines.push(current);
    return lines;
};

const DetailReport = ({ detailReport, amount, loading, error }) => {
    const typeRows = (salesByType) => [
        ['Dine In', salesByType?.dineIn],
        ['Take Away', salesByType?.takeaway],
        ['Food Panda', salesByType?.foodpanda],
        ['Delivery', salesByType?.orderKarachi],
        ['Employee Sales', salesByType?.employee]
    ];
    const renderRows = (salesByType) => typeRows(salesByType).map(([label, value]) => (
        <TableRow key={label}><TableCell>{label}</TableCell><TableCell align="right">{amount(value)}</TableCell></TableRow>
    ));
    const totalSalesAmount = typeRows(detailReport?.salesByType)
        .reduce((sum, [, value]) => sum + Number(value || 0), 0);

    return <Box sx={{ mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {loading && <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}><CircularProgress size={18} /><Typography variant="body2">Loading detail report...</Typography></Stack>}
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.25, bgcolor: 'primary.main', color: 'primary.contrastText' }}><Typography variant="subtitle1" align="center" sx={{ fontWeight: 800 }}>TOTAL SALES</Typography></Box>
            <TableContainer><Table size="small"><TableBody>
                {renderRows(detailReport?.salesByType)}
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 900 }}>TOTAL SALES AMOUNT</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>{amount(totalSalesAmount)}</TableCell>
                </TableRow>
            </TableBody></Table></TableContainer>
        </Paper>
        {(detailReport?.users || []).map((detailUser) => <Paper key={detailUser.userID} variant="outlined" sx={{ mt: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.15, bgcolor: 'grey.100' }}><Typography variant="subtitle1" sx={{ fontWeight: 800 }}>User ID: {detailUser.userID}{detailUser.userName ? ' - ' + detailUser.userName : ''}</Typography></Box>
            <TableContainer><Table size="small"><TableBody>
                {renderRows(detailUser.salesByType)}
                <TableRow><TableCell colSpan={2} sx={{ py: 0.75, borderBottom: 0 }} /></TableRow>
                {(detailUser.paymentMethods || []).map((payment) => <TableRow key={payment.method}><TableCell sx={{ fontWeight: 600 }}>{payment.method}</TableCell><TableCell align="right" sx={{ fontWeight: 600 }}>{amount(payment.amount)}</TableCell></TableRow>)}
                <TableRow><TableCell sx={{ borderTop: '2px solid', borderColor: 'divider', fontWeight: 700 }}>Recorded Payments Total</TableCell><TableCell align="right" sx={{ borderTop: '2px solid', borderColor: 'divider', fontWeight: 700 }}>{amount(detailUser.recordedPaymentTotal)}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 800 }}>Reconciled Payment Total</TableCell><TableCell align="right" sx={{ fontWeight: 800 }}>{amount(detailUser.paymentTotal)}</TableCell></TableRow>
                {Math.abs(Number(detailUser.paymentDifference || 0)) > 0.009 && <TableRow sx={{ bgcolor: 'warning.50' }}><TableCell sx={{ fontWeight: 700 }}>Sales - Payments Difference</TableCell><TableCell align="right" sx={{ fontWeight: 700, color: 'warning.dark' }}>{amount(detailUser.paymentDifference)}</TableCell></TableRow>}
                <TableRow><TableCell sx={{ borderTop: '2px solid', borderColor: 'divider', fontWeight: 700 }}>Opening Cash</TableCell><TableCell align="right" sx={{ borderTop: '2px solid', borderColor: 'divider', fontWeight: 700 }}>{amount(detailUser.openingAmount)}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 700 }}>Cash Sales (Payment Breakup)</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>{amount(detailUser.cashSales)}</TableCell></TableRow>
                <TableRow sx={{ bgcolor: 'success.50' }}><TableCell sx={{ fontWeight: 900 }}>Expected Cash in Till (Opening + Cash Sales)</TableCell><TableCell align="right" sx={{ fontWeight: 900 }}>{amount(detailUser.expectedCash)}</TableCell></TableRow>
            </TableBody></Table></TableContainer>
        </Paper>)}
        {!loading && (detailReport?.users || []).length === 0 && <Alert severity="info" sx={{ mt: 2 }}>Selected filters ke liye detail sales data available nahi hai.</Alert>}
    </Box>;
};

const EmployeeSalesReport = ({ employeeSales, amount, loading, error }) => (
    <Box sx={{ mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {loading && <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}><CircularProgress size={18} /><Typography variant="body2">Loading employee sales...</Typography></Stack>}
        {(employeeSales || []).map((employee) => (
            <Paper key={employee.employeeID} variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.25, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontWeight: 800 }}>{employee.employeeName}</Typography>
                        <Typography sx={{ fontWeight: 800 }}>{amount(employee.totalAmount)}</Typography>
                    </Stack>
                    <Typography variant="caption">Orders: {employee.orderCount}</Typography>
                </Box>
                <TableContainer><Table size="small">
                    <TableHead><TableRow>
                        <TableCell>Order / Date</TableCell>
                        <TableCell>Items</TableCell>
                        <TableCell align="right">Amount</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {(employee.orders || []).map((order) => (
                            <TableRow key={order.orderID}>
                                <TableCell>
                                    <Typography fontWeight={700}>#{order.orderID}</Typography>
                                    <Typography variant="caption">{order.entDate ? dayjs(order.entDate).format('DD-MMM-YYYY') : '-'}</Typography>
                                </TableCell>
                                <TableCell>
                                    {(order.items || []).map((item) => (
                                        <Typography key={item.orderItemID || `${item.itemCode}-${item.itemName}`} variant="body2">
                                            {item.itemName} x {Number(item.quantity || item.soldQty || 0)}
                                        </Typography>
                                    ))}
                                </TableCell>
                                <TableCell align="right">{amount(order.grandTotal)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table></TableContainer>
            </Paper>
        ))}
        {!loading && (employeeSales || []).length === 0 && (
            <Alert severity="info">Selected filters ke liye employee sales available nahi hain.</Alert>
        )}
    </Box>
);

const OrderTakerSalesReport = ({ orderTakerSales, amount, loading, error }) => (
    <Box sx={{ mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {loading && <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}><CircularProgress size={18} /><Typography variant="body2">Loading Order Taker sales...</Typography></Stack>}
        {(orderTakerSales || []).length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.25, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    <Typography align="center" sx={{ fontWeight: 800 }}>ORDER TAKER SALES SUMMARY</Typography>
                </Box>
                <TableContainer><Table size="small">
                    <TableHead><TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>Order Taker</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>No. of Orders</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>Amount</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>Avg / Order</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {(orderTakerSales || []).map((orderTaker) => (
                            <TableRow key={`summary-${orderTaker.orderTakerID}`}>
                                <TableCell>{orderTaker.orderTakerName}</TableCell>
                                <TableCell align="right">{orderTaker.orderCount || 0}</TableCell>
                                <TableCell align="right">{amount(orderTaker.totalAmount)}</TableCell>
                                <TableCell align="right">
                                    {amount(Number(orderTaker.totalAmount || 0) / Math.max(Number(orderTaker.orderCount || 0), 1))}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table></TableContainer>
            </Paper>
        )}
        {(orderTakerSales || []).map((orderTaker) => (
            <Paper key={orderTaker.orderTakerID} variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.25, bgcolor: 'info.main', color: 'info.contrastText' }}>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontWeight: 800 }}>{orderTaker.orderTakerName}</Typography>
                        <Typography sx={{ fontWeight: 800 }}>{amount(orderTaker.totalAmount)}</Typography>
                    </Stack>
                    <Typography variant="caption">Orders Taken: {orderTaker.orderCount}</Typography>
                </Box>
                <TableContainer><Table size="small">
                    <TableHead><TableRow>
                        <TableCell>Order / Date</TableCell>
                        <TableCell>Items</TableCell>
                        <TableCell align="right">Amount</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {(orderTaker.orders || []).map((order) => (
                            <TableRow key={order.orderID}>
                                <TableCell>
                                    <Typography fontWeight={700}>#{order.orderID}</Typography>
                                    <Typography variant="caption">
                                        {order.entDate ? dayjs(order.entDate).format('DD-MMM-YYYY') : '-'}
                                        {order.orderType ? ` | ${String(order.orderType).toUpperCase()}` : ''}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {(order.items || []).map((item) => (
                                        <Typography key={item.orderItemID || `${item.itemCode}-${item.itemName}`} variant="body2">
                                            {item.itemName} x {Number(item.quantity || item.soldQty || 0)}
                                        </Typography>
                                    ))}
                                </TableCell>
                                <TableCell align="right">{amount(order.grandTotal)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table></TableContainer>
            </Paper>
        ))}
        {!loading && (orderTakerSales || []).length === 0 && (
            <Alert severity="info">Selected filters ke liye Order Taker sales available nahi hain.</Alert>
        )}
    </Box>
);

const UnallocatedOrdersReport = ({ orders, amount, loading, error }) => (
    <Box sx={{ mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {loading && <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}><CircularProgress size={18} /><Typography variant="body2">Loading adjustments...</Typography></Stack>}
        <Alert severity="info" sx={{ mb: 1.5 }}>Only orders with a payment difference are shown. Delivery orders are excluded.</Alert>
        <Paper variant="outlined">
            <Box sx={{ px: 2, py: 1.25 }}><Typography sx={{ fontWeight: 800 }}>UNALLOCATED / ADJUSTMENT ORDER DETAILS</Typography></Box>
            <TableContainer sx={{ maxHeight: '65vh' }}><Table stickyHeader size="small">
                <TableHead><TableRow><TableCell>Order / Date</TableCell><TableCell>User</TableCell><TableCell>Type / Table</TableCell><TableCell>Payment Methods</TableCell><TableCell align="right">Order Total</TableCell><TableCell align="right">Recorded Payments</TableCell><TableCell align="right">Adjustment</TableCell></TableRow></TableHead>
                <TableBody>
                    {(orders || []).map((order) => (
                        <TableRow key={order.orderID}>
                            <TableCell><Typography fontWeight={700}>#{order.orderID}</Typography><Typography variant="caption">{order.entDate ? dayjs(order.entDate).format('DD-MMM-YYYY') : '-'}</Typography></TableCell>
                            <TableCell>{order.userName || ('User ' + (order.userID || '-'))}</TableCell>
                            <TableCell><Typography variant="body2">{String(order.orderType || '-').toUpperCase()}</Typography><Typography variant="caption" color="text.secondary">{order.tableLabel || 'N/A'}</Typography></TableCell>
                            <TableCell>{(order.paymentMethods || []).length > 0 ? order.paymentMethods.map((payment, index) => (<Typography key={order.orderID + '-payment-' + index} variant="body2">{payment.method}: {amount(payment.amount)}</Typography>)) : <Typography variant="caption" color="text.secondary">No payment recorded</Typography>}</TableCell>
                            <TableCell align="right">{amount(order.grandTotal)}</TableCell><TableCell align="right">{amount(order.recordedPaymentTotal)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: Math.abs(Number(order.unallocatedAmount || 0)) > 0.009 ? 'error.main' : 'inherit' }}>{amount(order.unallocatedAmount)}</TableCell>
                        </TableRow>
                    ))}
                    {!loading && (orders || []).length === 0 && (<TableRow><TableCell colSpan={7} align="center">Selected filters ke liye koi unallocated / adjustment order nahi hai.</TableCell></TableRow>)}
                </TableBody>
            </Table></TableContainer>
        </Paper>
    </Box>
);

const DiscountsReport = ({ rows, amount, loading, error }) => {
    const discountRows = (rows || []).map((row) => {
        const itemDiscount = (row.items || []).reduce((total, item) => total + Number(item.lineDiscount || 0), 0);
        const overallDiscount = Number(row.overallDiscount || 0);
        const couponDiscount = Number(row.couponAmount || 0);
        return { ...row, itemDiscount, overallDiscount, couponDiscount, totalDiscount: itemDiscount + overallDiscount + couponDiscount };
    }).filter((row) => row.totalDiscount > 0.009);
    const totals = discountRows.reduce((acc, row) => ({
        item: acc.item + row.itemDiscount, overall: acc.overall + row.overallDiscount,
        coupon: acc.coupon + row.couponDiscount, total: acc.total + row.totalDiscount
    }), { item: 0, overall: 0, coupon: 0, total: 0 });

    return <Box sx={{ mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {loading && <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}><CircularProgress size={18} /><Typography variant="body2">Loading discounts...</Typography></Stack>}
        <Grid2 container spacing={2} sx={{ mb: 2 }}>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}><Card><CardContent><Typography variant="caption">Discounted Orders</Typography><Typography variant="h6">{discountRows.length}</Typography></CardContent></Card></Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}><Card><CardContent><Typography variant="caption">Item Discounts</Typography><Typography variant="h6">{amount(totals.item)}</Typography></CardContent></Card></Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}><Card><CardContent><Typography variant="caption">Overall + Coupon</Typography><Typography variant="h6">{amount(totals.overall + totals.coupon)}</Typography></CardContent></Card></Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}><Card><CardContent><Typography variant="caption">Total Discounts</Typography><Typography variant="h6">{amount(totals.total)}</Typography></CardContent></Card></Grid2>
        </Grid2>
        <Paper><Box sx={{ px: 2, py: 1.25 }}><Typography sx={{ fontWeight: 800 }}>DISCOUNT DETAILS</Typography></Box>
            <TableContainer sx={{ maxHeight: '65vh' }}><Table stickyHeader size="small">
                <TableHead><TableRow><TableCell>Order</TableCell><TableCell>Date</TableCell><TableCell>Type</TableCell><TableCell>Customer</TableCell><TableCell align="right">Subtotal</TableCell><TableCell align="right">Item Discount</TableCell><TableCell align="right">Overall Discount</TableCell><TableCell>Coupon</TableCell><TableCell align="right">Coupon Discount</TableCell><TableCell align="right">Total Discount</TableCell><TableCell align="right">Grand Total</TableCell></TableRow></TableHead>
                <TableBody>
                    {discountRows.map((row) => <TableRow key={`discount-${row.orderID}`} hover>
                        <TableCell sx={{ fontWeight: 700 }}>#{row.orderID}</TableCell><TableCell>{row.entDate ? dayjs(row.entDate).format('DD-MMM-YYYY') : '-'}</TableCell><TableCell>{String(row.orderType || '-').toUpperCase()}</TableCell><TableCell>{row.customerName || '-'}</TableCell>
                        <TableCell align="right">{amount(row.subtotal)}</TableCell><TableCell align="right">{amount(row.itemDiscount)}</TableCell><TableCell align="right">{amount(row.overallDiscount)}</TableCell><TableCell>{row.couponCode || '-'}</TableCell><TableCell align="right">{amount(row.couponDiscount)}</TableCell><TableCell align="right" sx={{ fontWeight: 800 }}>{amount(row.totalDiscount)}</TableCell><TableCell align="right">{amount(row.grandTotal)}</TableCell>
                    </TableRow>)}
                    {!loading && discountRows.length === 0 && <TableRow><TableCell colSpan={11} align="center">Selected filters ke liye koi discount record nahi hai.</TableCell></TableRow>}
                </TableBody>
            </Table></TableContainer>
        </Paper>
    </Box>;
};

const SalesReport = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const { reportRows, summary, paymentMethodOptions, userOptions, itemConsumptionSummary, categorySalesSummary, detailReport, loading, error } = useSelector((state) => state.sr);

    const [filters, setFilters] = useState(defaultFilters);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [expandedOrders, setExpandedOrders] = useState({});
    const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
    const [activeReportTab, setActiveReportTab] = useState('summary');
    const [thermalReportTab, setThermalReportTab] = useState('summary');
    const [deleteSalesEnabled, setDeleteSalesEnabled] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [deletingSales, setDeletingSales] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState(null);

    useEffect(() => {
        if (!user?.userID) return;
        dispatch(getAdvancedSalesReport({
            userID: user.userID,
            ...defaultFilters
        }));
    }, [dispatch, user]);

    useEffect(() => {
        fetchPosOrderTypeSettings()
            .then((settings) => setDeleteSalesEnabled(Boolean(settings.enableDeleteAllSales)))
            .catch(() => setDeleteSalesEnabled(false));
    }, []);

    const handleDeleteAllSales = async () => {
        if (deleteConfirmation !== 'DELETE ALL SALES') return;
        setDeletingSales(true);
        setDeleteMessage(null);
        try {
            const response = await axios.delete(`${global.SET_API_URL}sr/deleteAllSales`, {
                data: { confirmation: deleteConfirmation, userID: user?.userID },
            });
            setDeleteDialogOpen(false);
            setDeleteConfirmation('');
            setExpandedOrders({});
            setPage(0);
            setDeleteMessage({ severity: 'success', text: response.data?.message || 'All sales data has been deleted.' });
            dispatch(getAdvancedSalesReport({ userID: user.userID, ...filters }));
        } catch (deleteError) {
            setDeleteMessage({
                severity: 'error',
                text: deleteError?.response?.data?.message || 'Sales data could not be deleted.',
            });
        } finally {
            setDeletingSales(false);
        }
    };

    const visibleRows = useMemo(
        () => reportRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [reportRows, page, rowsPerPage]
    );

    const onChangeFilter = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const applyFilters = () => {
        if (!user?.userID) return;
        setPage(0);
        dispatch(getAdvancedSalesReport({ userID: user.userID, ...filters }));
    };

    const resetFilters = () => {
        const resetPayload = { ...defaultFilters };
        setFilters(resetPayload);
        setPage(0);
        setExpandedOrders({});
        if (!user?.userID) return;
        dispatch(getAdvancedSalesReport({ userID: user.userID, ...resetPayload }));
    };

    const handleToggleOrder = (orderID) => {
        setExpandedOrders((prev) => ({
            ...prev,
            [orderID]: !prev[orderID]
        }));
    };

    const handleExpandAllOrders = () => {
        const expandedMap = {};
        visibleRows.forEach((row) => {
            expandedMap[row.orderID] = true;
        });
        setExpandedOrders(expandedMap);
    };

    const handleToggleAllOrders = () => {
        const allOpen = visibleRows.length > 0 && visibleRows.every((row) => !!expandedOrders[row.orderID]);

        if (allOpen) {
            setExpandedOrders({});
            return;
        }

        handleExpandAllOrders();
    };

    const exportCSV = () => {
        if (!reportRows.length) return;

        const headers = [
            'Order ID',
            'Date',
            'Created At',
            'Completed At',
            'Order Type',
            'Order Status',
            'KOT #',
            'Ticket #',
            'Customer',
            'Phone',
            'Table',
            'Payment Methods',
            'Amount',
            'Aggregator',
            'Aggregator Order #',
            'Last Bill Printed At'
        ];

        const escapeValue = (value) => {
            const str = String(value ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const lines = reportRows.map((row) => [
            row.orderID,
            row.entDate ? dayjs(row.entDate).format('YYYY-MM-DD') : '',
            row.createdAt ? dayjs(row.createdAt).format('YYYY-MM-DD HH:mm:ss') : '',
            row.completedAt ? dayjs(row.completedAt).format('YYYY-MM-DD HH:mm:ss') : '',
            row.orderType,
            row.orderStatus,
            row.kotNumber,
            row.ticketNo,
            row.customerName,
            row.customerPhone,
            row.tableLabel,
            row.paymentMethods,
            Number(row.grandTotal || 0).toFixed(2),
            row.aggregatorPlatform,
            row.aggregatorOrderCode,
            row.lastBillPrintedAt ? dayjs(row.lastBillPrintedAt).format('YYYY-MM-DD HH:mm:ss') : ''
        ].map(escapeValue).join(','));

        const csvContent = [headers.join(','), ...lines].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `sales-report-${dayjs().format('YYYYMMDD-HHmm')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const amount = (value) => `Rs. ${Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;

    const areAllOrdersOpen = visibleRows.length > 0 && visibleRows.every((row) => !!expandedOrders[row.orderID]);

    const itemWiseTotalSalesSummary = useMemo(
        () => (itemConsumptionSummary || []).map((item) => ({
            itemCode: item.itemCode,
            itemName: item.itemName,
            reportingUnitName: item.reportingUnitName || 'Unit',
            totalQty: Number(item.totalEquivalentReportingQty || 0),
        })),
        [itemConsumptionSummary]
    );

    const categoryWiseSalesSummary = useMemo(
        () => (categorySalesSummary || []).map((category) => ({
            categoryCode: category.categoryCode,
            categoryName: category.categoryName || 'Uncategorized',
            totalQty: Number(category.totalQty || 0),
            totalAmount: Number(category.totalAmount || 0),
            items: (category.items || []).map((item) => ({
                itemCode: item.itemCode,
                itemName: item.itemName || 'Item',
                reportingUnitName: item.reportingUnitName || 'Unit',
                totalQty: Number(item.totalQty || 0),
                totalAmount: Number(item.totalAmount || 0),
            })),
        })),
        [categorySalesSummary]
    );



    const dealWiseSalesSummary = useMemo(() => {
        const deals = new Map();
        (reportRows || []).forEach((order) => {
            const orderDeals = new Map();
            (order.items || []).forEach((item) => {
                if (Number(item.isDealItem || 0) !== 1 || !item.dealID) return;
                const dealID = Number(item.dealID);
                const dealKey = Number.isFinite(dealID) && dealID > 0 ? `id:${dealID}` : `name:${String(item.dealName || item.itemName || 'Deal').trim()}`;
                const bundleQty = Number(item.dealBundleQty || 0);
                const safeBundleQty = Number.isFinite(bundleQty) && bundleQty > 0 ? bundleQty : 1;
                const previous = orderDeals.get(dealKey);
                if (!previous || safeBundleQty > previous.bundleQty) orderDeals.set(dealKey, { dealID: Number.isFinite(dealID) && dealID > 0 ? dealID : null, dealName: String(item.dealName || item.itemName || 'Deal').trim(), bundleQty: safeBundleQty });
            });
            orderDeals.forEach((deal, dealKey) => {
                if (!deals.has(dealKey)) deals.set(dealKey, { ...deal, totalQty: 0, orderCount: 0, orderIDs: [] });
                const summary = deals.get(dealKey);
                summary.totalQty += deal.bundleQty;
                summary.orderCount += 1;
                summary.orderIDs.push(Number(order.orderID));
            });
        });
        return Array.from(deals.values()).sort((left, right) => Number(right.totalQty || 0) - Number(left.totalQty || 0));
    }, [reportRows]);

    const dealWiseDetailRows = useMemo(() => {
        const details = new Map();
        (reportRows || []).forEach((order) => {
            const dealsInOrder = new Map();
            const orderType = String(order.aggregatorPlatform || '').trim().toLowerCase() === 'foodpanda'
                ? 'Foodpanda'
                : ({ dinein: 'Dine In', takeaway: 'Takeaway', dispatch: 'Dispatch', delivery: 'Delivery' }[String(order.orderType || '').trim().toLowerCase()] || 'Other');
            (order.items || []).forEach((item) => {
                if (Number(item.isDealItem || 0) !== 1 || !item.dealID) return;
                const dealID = Number(item.dealID);
                const dealKey = Number.isFinite(dealID) && dealID > 0 ? `id:${dealID}` : `name:${String(item.dealName || item.itemName || 'Deal').trim()}`;
                const bundleQty = Number(item.dealBundleQty || 0);
                const safeBundleQty = Number.isFinite(bundleQty) && bundleQty > 0 ? bundleQty : 1;
                if (!dealsInOrder.has(dealKey)) dealsInOrder.set(dealKey, { dealID: Number.isFinite(dealID) && dealID > 0 ? dealID : null, dealName: String(item.dealName || item.itemName || 'Deal').trim(), bundleQty: safeBundleQty, amount: 0 });
                const deal = dealsInOrder.get(dealKey);
                deal.bundleQty = Math.max(deal.bundleQty, safeBundleQty);
                deal.amount += Number(item.itemTotal || 0);
            });
            dealsInOrder.forEach((deal, dealKey) => {
                const detailKey = `${dealKey}:${orderType}`;
                if (!details.has(detailKey)) details.set(detailKey, { ...deal, orderType, totalQty: 0, orderCount: 0, totalAmount: 0, orderIDs: [] });
                const row = details.get(detailKey);
                row.totalQty += deal.bundleQty;
                row.orderCount += 1;
                row.totalAmount += deal.amount;
                row.orderIDs.push(Number(order.orderID));
            });
        });
        return Array.from(details.values()).sort((left, right) => String(left.dealName).localeCompare(String(right.dealName)) || String(left.orderType).localeCompare(String(right.orderType)));
    }, [reportRows]);

    const dealWiseDetailGroups = useMemo(() => {
        const groups = new Map();
        (dealWiseDetailRows || []).forEach((row) => {
            const key = row.dealID ? `id:${row.dealID}` : `name:${row.dealName}`;
            if (!groups.has(key)) groups.set(key, { dealID: row.dealID, dealName: row.dealName, rows: [], totalQty: 0, totalAmount: 0, orderIDs: new Set() });
            const group = groups.get(key);
            group.rows.push(row);
            group.totalQty += Number(row.totalQty || 0);
            group.totalAmount += Number(row.totalAmount || 0);
            (row.orderIDs || []).forEach((orderID) => group.orderIDs.add(orderID));
        });
        return Array.from(groups.values()).map((group) => ({ ...group, orderCount: group.orderIDs.size }));
    }, [dealWiseDetailRows]);

    const dealSalesOverview = useMemo(() => {
        const categories = {
            'Dine In': { label: 'Dine In', orderIDs: new Set(), amount: 0 },
            Takeaway: { label: 'Takeaway', orderIDs: new Set(), amount: 0 },
            Delivery: { label: 'Delivery', orderIDs: new Set(), amount: 0 },
            Foodpanda: { label: 'Foodpanda', orderIDs: new Set(), amount: 0 }
        };
        (dealWiseDetailRows || []).forEach((row) => {
            const category = row.orderType === 'Dispatch' || row.orderType === 'Delivery' ? 'Delivery' : row.orderType;
            if (!categories[category]) return;
            (row.orderIDs || []).forEach((orderID) => categories[category].orderIDs.add(orderID));
            categories[category].amount += Number(row.totalAmount || 0);
        });
        const rows = Object.values(categories).map((category) => ({ ...category, orderCount: category.orderIDs.size }));
        const allOrderIDs = new Set(rows.flatMap((row) => Array.from(row.orderIDs)));
        return { rows, totalOrders: allOrderIDs.size, totalAmount: rows.reduce((sum, row) => sum + row.amount, 0) };
    }, [dealWiseDetailRows]);

    const thermalPreviewText = useMemo(() => {
        const width = THERMAL_PREVIEW_WIDTH;
        const lines = [];
        const divider = '='.repeat(width);
        const thermalTabLabels = {
            summary: 'Summary',
            detail: 'Detail Report',
            discounts: 'Discounts',
            employee: 'Employee Sales',
            orderTaker: 'Order Taker Sales',
            category: 'Category Wise',
            item: 'Item Wise',
            orders: 'Order Wise',
            ordersWithItems: 'Orders With Items'
        };
        const addHeader = (title) => {
            lines.push(thermalCenter('Lucky Braost', width));
            lines.push(thermalCenter('Sales Report', width));
            lines.push(thermalCenter(title, width));
            lines.push(divider);
            lines.push(thermalLeftRight('Printed', dayjs().format('DD-MMM HH:mm'), width));
            lines.push(thermalLeftRight('Scope', filters.scope || 'today', width));
            if (filters.fromDate) {
                lines.push(thermalLeftRight('From', filters.fromDate, width));
            }
            if (filters.toDate) {
                lines.push(thermalLeftRight('To', filters.toDate, width));
            }
            if (filters.orderType && filters.orderType !== 'all') {
                lines.push(thermalLeftRight('Type', filters.orderType, width));
            }
            if (filters.orderStatus && filters.orderStatus !== 'all') {
                lines.push(thermalLeftRight('Status', filters.orderStatus, width));
            }
            lines.push(divider);
        };

        addHeader(thermalTabLabels[thermalReportTab] || 'Summary');

        if (thermalReportTab === 'summary') {
            lines.push(thermalCenter('ORDER SUMMARY', width));
            lines.push(divider);
            lines.push(thermalLeftRight('Total Orders', summary.totalOrders || 0, width));
            lines.push(thermalLeftRight('Completed', summary.completedOrders || 0, width));
            lines.push(thermalLeftRight('Active/Held', summary.activeOrders || 0, width));
            lines.push(thermalLeftRight('Draft', summary.draftOrders || 0, width));
            lines.push(thermalLeftRight('Total Amount', amount(summary.totalAmount), width));
            lines.push(thermalLeftRight('Avg Ticket', amount(summary.averageOrderAmount), width));
            lines.push(divider);
            lines.push(thermalCenter('SALES BY TYPE', width));
            lines.push(divider);
            lines.push(thermalLeftRight('Dine In', amount(summary.dineInAmount), width));
            lines.push(thermalLeftRight('Takeaway', amount(summary.takeawayAmount), width));
            lines.push(thermalLeftRight('Dispatch', amount(summary.dispatchAmount), width));
            lines.push(thermalLeftRight('Delivery', amount(summary.deliveryAmount), width));
            lines.push(thermalLeftRight('Foodpanda', amount(summary.foodpandaAmount), width));
            lines.push(thermalLeftRight('Employee Sales', amount(summary.employeeAmount), width));
        } else if (thermalReportTab === 'discounts') {
            const discountedOrders = (reportRows || []).map((row) => {
                const itemDiscount = (row.items || []).reduce((total, item) => total + Number(item.lineDiscount || 0), 0);
                const overallDiscount = Number(row.overallDiscount || 0);
                const couponDiscount = Number(row.couponAmount || 0);
                return { ...row, itemDiscount, overallDiscount, couponDiscount, totalDiscount: itemDiscount + overallDiscount + couponDiscount };
            }).filter((row) => row.totalDiscount > 0.009);
            const discountTotal = discountedOrders.reduce((total, row) => total + row.totalDiscount, 0);

            lines.push(thermalCenter('DISCOUNT DETAILS', width));
            lines.push(divider);
            lines.push(thermalLeftRight('Discounted Orders', discountedOrders.length, width));
            lines.push(thermalLeftRight('Total Discount', amount(discountTotal), width));
            lines.push(divider);
            if (discountedOrders.length === 0) {
                lines.push(thermalCenter('NO DISCOUNT RECORDS', width));
                lines.push(divider);
            } else {
                discountedOrders.forEach((row) => {
                    lines.push(thermalLeftRight(`#${row.orderID}`, row.entDate ? dayjs(row.entDate).format('DD-MMM') : '-', width));
                    lines.push(thermalLeftRight(String(row.orderType || '-').toUpperCase(), amount(row.grandTotal), width));
                    if (row.customerName) lines.push(thermalLeftRight('Customer', row.customerName, width));
                    if (row.tableLabel) lines.push(thermalLeftRight('Table', row.tableLabel, width));
                    lines.push('-'.repeat(width));
                    lines.push(thermalCenter('ITEMS', width));
                    if ((row.items || []).length === 0) {
                        lines.push(thermalCenter('NO ITEM DETAILS', width));
                    } else {
                        (row.items || []).forEach((item) => {
                            thermalWrap(String(item.itemName || item.itemCode || '-').toUpperCase(), width).forEach((itemLine, index) => {
                                lines.push(index === 0 ? itemLine : `  ${thermalClamp(itemLine, width - 2)}`);
                            });
                            lines.push(thermalLeftRight(
                                `Qty ${Number(item.quantity || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })} x ${Number(item.unitPrice || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`,
                                amount(item.itemTotal),
                                width
                            ));
                            if (Number(item.lineDiscount || 0) > 0.009) {
                                lines.push(thermalLeftRight('  Line Discount', amount(item.lineDiscount), width));
                            }
                        });
                    }
                    lines.push('-'.repeat(width));
                    lines.push(thermalLeftRight('Subtotal', amount(row.subtotal), width));
                    if (row.itemDiscount > 0.009) lines.push(thermalLeftRight('Item Discount', amount(row.itemDiscount), width));
                    if (row.overallDiscount > 0.009) lines.push(thermalLeftRight('Overall Discount', amount(row.overallDiscount), width));
                    if (row.couponDiscount > 0.009) lines.push(thermalLeftRight(`Coupon ${row.couponCode || ''}`.trim(), amount(row.couponDiscount), width));
                    lines.push(thermalLeftRight('Total Discount', amount(row.totalDiscount), width));
                    lines.push(thermalLeftRight('Grand Total', amount(row.grandTotal), width));
                    lines.push(divider);
                });
            }
        } else if (thermalReportTab === 'detail') {
            const detailTypeRows = [
                ['Dine In', detailReport?.salesByType?.dineIn],
                ['Take Away', detailReport?.salesByType?.takeaway],
                ['Food Panda', detailReport?.salesByType?.foodpanda],
                ['Delivery', detailReport?.salesByType?.orderKarachi],
                ['Employee Sales', detailReport?.salesByType?.employee]
            ];
            lines.push(thermalCenter('TOTAL SALES', width));
            lines.push(divider);
            detailTypeRows.forEach(([label, value]) => lines.push(thermalLeftRight(label, amount(value), width)));
            lines.push('-'.repeat(width));
            lines.push(thermalLeftRight(
                'TOTAL SALES AMOUNT',
                amount(detailTypeRows.reduce((sum, [, value]) => sum + Number(value || 0), 0)),
                width
            ));
            lines.push(thermalLeftRight('No of Orders', summary.totalOrders || 0, width));

            (detailReport?.users || []).forEach((detailUser) => {
                lines.push(divider);
                lines.push(thermalCenter('USER ID: ' + detailUser.userID, width));
                if (detailUser.userName) lines.push(thermalCenter(detailUser.userName, width));
                lines.push(divider);
                [
                    ['Dine In', detailUser.salesByType?.dineIn],
                    ['Take Away', detailUser.salesByType?.takeaway],
                    ['Food Panda', detailUser.salesByType?.foodpanda],
                    ['Delivery', detailUser.salesByType?.orderKarachi],
                    ['Employee Sales', detailUser.salesByType?.employee]
                ].forEach(([label, value]) => lines.push(thermalLeftRight(label, amount(value), width)));
                if ((detailUser.paymentMethods || []).length > 0) {
                    lines.push('-'.repeat(width));
                    detailUser.paymentMethods.forEach((payment) => {
                        lines.push(thermalLeftRight(payment.method, amount(payment.amount), width));
                    });
                }
                lines.push('-'.repeat(width));
                lines.push(thermalLeftRight('Recorded Payments', amount(detailUser.recordedPaymentTotal), width));
                lines.push('-'.repeat(width));
                lines.push(thermalLeftRight('Opening Cash', amount(detailUser.openingAmount), width));
                lines.push(thermalLeftRight('Cash Sales', amount(detailUser.cashSales), width));
                lines.push(thermalLeftRight('EXPECTED CASH', amount(detailUser.expectedCash), width));
            });

            if ((detailReport?.users || []).length === 0) {
                lines.push('No detail sales data');
            }
        } else if (thermalReportTab === 'employee') {
            lines.push(thermalCenter('EMPLOYEE-WISE SALES', width));
            lines.push(divider);
            if ((detailReport?.employeeSales || []).length === 0) {
                lines.push('No employee sales data');
            } else {
                detailReport.employeeSales.forEach((employee) => {
                    lines.push(thermalClamp(String(employee.employeeName || 'Unknown Employee').toUpperCase(), width));
                    lines.push(thermalLeftRight('Orders', employee.orderCount || 0, width));
                    lines.push(thermalLeftRight('Employee Total', amount(employee.totalAmount), width));
                    (employee.orders || []).forEach((order) => {
                        lines.push('-'.repeat(width));
                        lines.push(thermalLeftRight(`Order #${order.orderID || '-'}`, amount(order.grandTotal), width));
                        lines.push(thermalLeftRight(
                            'Date',
                            order.entDate ? dayjs(order.entDate).format('DD-MMM-YYYY') : '-',
                            width
                        ));
                        (order.items || []).forEach((item) => {
                            const qty = Number(item.quantity || item.soldQty || 0);
                            thermalWrap(`${item.itemName || 'Item'} x ${qty}`, width)
                                .forEach((line) => lines.push(line));
                            lines.push(thermalLeftRight('  Item Amount', amount(item.itemTotal), width));
                        });
                    });
                    lines.push(divider);
                });
            }
        } else if (thermalReportTab === 'orderTaker') {
            lines.push(thermalCenter('ORDER TAKER-WISE SALES', width));
            lines.push(divider);
            if ((detailReport?.orderTakerSales || []).length === 0) {
                lines.push('No Order Taker sales data');
            } else {
                lines.push(thermalCenter('SUMMARY', width));
                lines.push(divider);
                detailReport.orderTakerSales.forEach((orderTaker) => {
                    lines.push(thermalClamp(String(orderTaker.orderTakerName || 'Unknown Order Taker').toUpperCase(), width));
                    lines.push(thermalLeftRight('No. of Orders', orderTaker.orderCount || 0, width));
                    lines.push(thermalLeftRight('Amount', amount(orderTaker.totalAmount), width));
                    lines.push(thermalLeftRight(
                        'Avg / Order',
                        amount(Number(orderTaker.totalAmount || 0) / Math.max(Number(orderTaker.orderCount || 0), 1)),
                        width
                    ));
                    lines.push('-'.repeat(width));
                });
                lines.push(thermalCenter('ORDER DETAILS', width));
                lines.push(divider);
                detailReport.orderTakerSales.forEach((orderTaker) => {
                    lines.push(thermalClamp(String(orderTaker.orderTakerName || 'Unknown Order Taker').toUpperCase(), width));
                    lines.push(thermalLeftRight('Orders Taken', orderTaker.orderCount || 0, width));
                    lines.push(thermalLeftRight('Sales Total', amount(orderTaker.totalAmount), width));
                    (orderTaker.orders || []).forEach((order) => {
                        lines.push('-'.repeat(width));
                        lines.push(thermalLeftRight(`Order #${order.orderID || '-'}`, amount(order.grandTotal), width));
                        lines.push(thermalLeftRight(
                            order.orderType ? String(order.orderType).toUpperCase() : 'Order',
                            order.entDate ? dayjs(order.entDate).format('DD-MMM-YYYY') : '-',
                            width
                        ));
                        (order.items || []).forEach((item) => {
                            const qty = Number(item.quantity || item.soldQty || 0);
                            thermalWrap(`${item.itemName || 'Item'} x ${qty}`, width)
                                .forEach((line) => lines.push(line));
                            lines.push(thermalLeftRight('  Item Amount', amount(item.itemTotal), width));
                        });
                    });
                    lines.push(divider);
                });
            }
        } else if (thermalReportTab === 'category') {
            lines.push(thermalCenter('CATEGORY WISE SALES', width));
            lines.push(divider);
            if (categoryWiseSalesSummary.length === 0) {
                lines.push('No category wise sales data');
            } else {
                categoryWiseSalesSummary.forEach((category) => {
                    lines.push(
                        thermalLeftRight(
                            String(category.categoryName || '-').toUpperCase(),
                            amount(category.totalAmount),
                            width
                        )
                    );
                    lines.push(thermalLeftRight('Category Qty', category.totalQty.toLocaleString('en-US', { maximumFractionDigits: 2 }), width));

                    if ((category.items || []).length === 0) {
                        lines.push('  No item sales data');
                    } else {
                        category.items.forEach((item) => {
                            lines.push(
                                thermalLeftRight(
                                    '  ' + String(item.itemName || '-').toUpperCase(),
                                    amount(item.totalAmount),
                                    width
                                )
                            );
                            lines.push(
                                thermalLeftRight(
                                    '    Qty',
                                    item.totalQty.toLocaleString('en-US', { maximumFractionDigits: 2 }) + ' ' + item.reportingUnitName,
                                    width
                                )
                            );
                        });
                    }
                    lines.push(divider);
                });
            }
        } else if (thermalReportTab === 'item') {
            lines.push(thermalCenter('ITEM WISE TOTAL SALES', width));
            lines.push(divider);
            if (itemWiseTotalSalesSummary.length === 0) {
                lines.push('No item wise sales data');
            } else {
                itemWiseTotalSalesSummary.forEach((item) => {
                    lines.push(
                        thermalLeftRight(
                            String(item.itemName || '-').toUpperCase(),
                            `${item.totalQty.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${item.reportingUnitName}`,
                            width
                        )
                    );
                    lines.push(divider);
                });
            }

            lines.push(thermalCenter('ITEM CONSUMPTION', width));
            lines.push(divider);
            if ((itemConsumptionSummary || []).length === 0) {
                lines.push('No item consumption data');
            } else {
                itemConsumptionSummary.forEach((item) => {
                    lines.push(thermalClamp(String(item.itemName || '-').toUpperCase(), width));
                    lines.push(
                        thermalLeftRight(
                            `Sep ${Number(item.separateSoldQty || 0).toLocaleString()}`,
                            `${Number(item.dealBaseUnitQty || 0).toLocaleString()} ${item.baseUnitName || 'Unit'}`,
                            width
                        )
                    );
                    lines.push(
                        thermalLeftRight(
                            `Deal Eqv ${Number(item.dealEquivalentReportingQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`,
                            `Tot ${Number(item.totalEquivalentReportingQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`,
                            width
                        )
                    );
                    thermalWrap(
                        `${Number(item.baseUnitsPerReportingUnit || 1)} ${item.baseUnitName || 'Unit'} = 1 ${item.reportingUnitName || 'Unit'}`,
                        width
                    ).forEach((line) => lines.push(line));
                    lines.push(divider);
                });
            }
        } else if (thermalReportTab === 'orders') {
            lines.push(thermalCenter('ORDER WISE SALES', width));
            lines.push(divider);
            if ((reportRows || []).length === 0) {
                lines.push('No order wise sales data');
            } else {
                reportRows.forEach((row) => {
                    const orderTypeText = String(row.aggregatorPlatform || '').toLowerCase() === 'foodpanda'
                        ? 'Foodpanda'
                        : String(row.orderType || '-').toUpperCase();
                    lines.push(thermalLeftRight(`#${row.orderID || '-'}`, amount(row.grandTotal), width));
                    lines.push(thermalLeftRight(orderTypeText, String(row.orderStatus || '-').toUpperCase(), width));
                    lines.push(thermalLeftRight('Date', row.entDate ? dayjs(row.entDate).format('DD-MMM-YYYY') : '-', width));
                    if (row.kotNumber || row.ticketNo) {
                        lines.push(thermalLeftRight(`KOT ${row.kotNumber || '-'}`, `Ticket ${row.ticketNo || '-'}`, width));
                    }
                    if (row.paymentMethods) {
                        lines.push(thermalLeftRight('Payment', row.paymentMethods, width));
                    }
                    if (row.customerName) {
                        thermalWrap(`Customer: ${row.customerName}`, width).forEach((line) => lines.push(line));
                    }
                    lines.push(divider);
                });
            }
        } else if (thermalReportTab === 'ordersWithItems') {
            lines.push(thermalCenter('ORDERS WITH ITEMS', width));
            lines.push(divider);
            if ((reportRows || []).length === 0) {
                lines.push('No order item details found');
            } else {
                reportRows.forEach((row) => {
                    const orderTypeText = String(row.aggregatorPlatform || '').toLowerCase() === 'foodpanda'
                        ? 'Foodpanda'
                        : String(row.orderType || '-').toUpperCase();
                    lines.push(thermalLeftRight(`#${row.orderID || '-'}`, amount(row.grandTotal), width));
                    lines.push(thermalLeftRight(orderTypeText, String(row.orderStatus || '-').toUpperCase(), width));
                    lines.push(thermalLeftRight('Date', row.entDate ? dayjs(row.entDate).format('DD-MMM-YYYY') : '-', width));
                    if (row.kotNumber || row.ticketNo) {
                        lines.push(thermalLeftRight(`KOT ${row.kotNumber || '-'}`, `Ticket ${row.ticketNo || '-'}`, width));
                    }
                    if (row.paymentMethods) {
                        lines.push(thermalLeftRight('Payment', row.paymentMethods, width));
                    }
                    lines.push('-'.repeat(width));
                    if ((row.items || []).length === 0) {
                        lines.push('  No item details');
                    } else {
                        (row.items || []).forEach((item) => {
                            thermalWrap(String(item.itemName || '-').toUpperCase(), width).forEach((line, index) => {
                                lines.push(index === 0 ? line : `  ${thermalClamp(line, width - 2)}`);
                            });
                            lines.push(
                                thermalLeftRight(
                                    `Qty ${Number(item.quantity || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`,
                                    amount(item.itemTotal),
                                    width
                                )
                            );
                            lines.push(
                                thermalLeftRight(
                                    `Rate ${Number(item.unitPrice || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`,
                                    `Disc ${Number(item.lineDiscount || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`,
                                    width
                                )
                            );
                        });
                    }
                    lines.push(divider);
                });
            }
        }

        lines.push(thermalCenter('PREVIEW ONLY', width));
        return lines.join('\n');
    }, [categoryWiseSalesSummary, filters, itemConsumptionSummary, itemWiseTotalSalesSummary, reportRows, summary, detailReport, thermalReportTab]);
    const handleOpenPrintPreview = () => {
        if (!reportRows.length) return;
        setThermalReportTab(activeReportTab);
        setPrintPreviewOpen(true);
    };

    const handleClosePrintPreview = () => {
        setPrintPreviewOpen(false);
    };

    const handlePrintThermal = () => {
        const popup = window.open('', '_blank', 'width=420,height=760');
        if (!popup) return;

        const printableLines = thermalPreviewText.split('\n').map((line) => {
            const layout = parseThermalLine(line);
            if (layout.type === 'divider') return '<div class="divider"></div>';
            if (layout.type === 'blank') return '<div class="blank">&nbsp;</div>';
            if (layout.type === 'row') {
                return `<div class="report-row"><span>${escapePrintHtml(layout.left)}</span><span>${escapePrintHtml(layout.right)}</span></div>`;
            }
            return `<div class="${layout.type === 'heading' ? 'heading' : 'report-text'}">${escapePrintHtml(layout.text)}</div>`;
        }).join('');

        popup.document.write(`
            <html>
                <head>
                    <title>Sales Report Thermal Print</title>
                    <style>
                        @page { size: 80mm auto; margin: 4mm; }
                        body { margin: 0; padding: 0; font-family: 'Times New Roman', Times, serif; }
                        .sheet { width: 72mm; margin: 0 auto; padding: 4mm 0; }
                        .sheet { font-size: 13px; line-height: 1.32; }
                        .report-row { display: flex; justify-content: space-between; gap: 3mm; width: 100%; }
                        .report-row span:first-child { text-align: left; }
                        .report-row span:last-child { text-align: right; margin-left: auto; white-space: nowrap; }
                        .heading { text-align: center; font-weight: 700; margin: 0.5mm 0; }
                        .report-text { text-align: left; }
                        .divider { border-top: 0.25mm solid #111; margin: 1mm 0; }
                        .blank { height: 1.5mm; }
                    </style>
                </head>
                <body>
                    <div class="sheet">${printableLines}</div>
                    <script>
                        window.onload = function() {
                            window.focus();
                            window.print();
                        };
                    </script>
                </body>
            </html>
        `);
        popup.document.close();
    };

    const handleDownloadThermalPdf = () => {
        const lines = thermalPreviewText.split('\n');
        const lineHeight = 4;
        const topMargin = 6;
        const bottomMargin = 6;
        const pageHeight = Math.max(80, topMargin + bottomMargin + (lines.length * lineHeight));

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: [80, pageHeight]
        });

        doc.setFont('times', 'normal');
        doc.setFontSize(10.5);

        let y = topMargin;
        lines.forEach((line) => {
            const layout = parseThermalLine(line);
            if (layout.type === 'divider') {
                doc.setLineWidth(0.25);
                doc.line(4, y - 1, 76, y - 1);
            } else if (layout.type === 'row') {
                doc.setFont('times', 'normal');
                doc.text(layout.left, 4, y);
                doc.text(layout.right, 76, y, { align: 'right' });
            } else if (layout.type === 'heading') {
                doc.setFont('times', 'bold');
                doc.text(layout.text, 40, y, { align: 'center' });
            } else if (layout.type === 'text') {
                doc.setFont('times', 'normal');
                doc.text(layout.text, 4, y);
            }
            y += lineHeight;
        });

        doc.save(`sales-report-thermal-${thermalReportTab}-${dayjs().format('YYYYMMDD-HHmm')}.pdf`);
    };

    return (
        <>
            <Navbar />
            <Container maxWidth="xl">
                <Box sx={{ p: 2 }}>
                    <HeadingText text="Sales Report" />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Advanced order-level sales report with completed and active order visibility.
                    </Typography>

                    {deleteMessage && (
                        <Alert severity={deleteMessage.severity} sx={{ mt: 2 }} onClose={() => setDeleteMessage(null)}>
                            {deleteMessage.text}
                        </Alert>
                    )}

                    {deleteSalesEnabled && (
                        <Button
                            color='error'
                            variant='contained'
                            startIcon={<DeleteForeverIcon />}
                            sx={{ mt: 2 }}
                            onClick={() => setDeleteDialogOpen(true)}
                        >
                            Delete All Sales
                        </Button>
                    )}

                    <Dialog open={deleteDialogOpen} onClose={() => !deletingSales && setDeleteDialogOpen(false)} maxWidth='sm' fullWidth>
                        <DialogTitle>Delete All Sales Permanently?</DialogTitle>
                        <DialogContent>
                            <Alert severity='error' sx={{ mb: 2 }}>
                                Yeh action undo nahi ho sakta. Tamam orders aur unka related sales data permanently delete ho jayega.
                            </Alert>
                            <Typography variant='body2' sx={{ mb: 1 }}>
                                Confirm karne ke liye DELETE ALL SALES type karein:
                            </Typography>
                            <TextField
                                fullWidth
                                value={deleteConfirmation}
                                onChange={(event) => setDeleteConfirmation(event.target.value)}
                                disabled={deletingSales}
                                autoComplete='off'
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmation(''); }} disabled={deletingSales}>
                                Cancel
                            </Button>
                            <Button
                                color='error'
                                variant='contained'
                                onClick={handleDeleteAllSales}
                                disabled={deletingSales || deleteConfirmation !== 'DELETE ALL SALES'}
                            >
                                {deletingSales ? 'Deleting...' : 'Delete All Sales'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Stack spacing={2}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Advanced Filters</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            variant="contained"
                                            startIcon={<FilterAltIcon />}
                                            onClick={applyFilters}
                                            disabled={loading}
                                        >
                                            Apply
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<RestartAltIcon />}
                                            onClick={resetFilters}
                                            disabled={loading}
                                        >
                                            Reset
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<DownloadForOfflineIcon />}
                                            onClick={exportCSV}
                                            disabled={!reportRows.length}
                                        >
                                            Export CSV
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={handleToggleAllOrders}
                                            disabled={!visibleRows.length}
                                        >
                                            {areAllOrdersOpen ? 'Hide All Orders' : 'Show All Orders'}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<PrintIcon />}
                                            onClick={handleOpenPrintPreview}
                                            disabled={!reportRows.length}
                                        >
                                            Print / PDF
                                        </Button>
                                    </Stack>
                                </Stack>

                                <Grid2 container spacing={2}>
                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Scope</InputLabel>
                                            <Select
                                                label="Scope"
                                                value={filters.scope}
                                                onChange={(e) => onChangeFilter('scope', e.target.value)}
                                            >
                                                <MenuItem value="today">Today</MenuItem>
                                                <MenuItem value="lastDay">Last Day</MenuItem>
                                                <MenuItem value="last7days">Last 7 Days</MenuItem>
                                                <MenuItem value="last30days">Last 30 Days</MenuItem>
                                                <MenuItem value="opening">Current Opening</MenuItem>
                                                <MenuItem value="custom">Custom Range</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            label="From Date"
                                            value={filters.fromDate}
                                            onChange={(e) => onChangeFilter('fromDate', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            label="To Date"
                                            value={filters.toDate}
                                            onChange={(e) => onChangeFilter('toDate', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Order Type</InputLabel>
                                            <Select
                                                label="Order Type"
                                                value={filters.orderType}
                                                onChange={(e) => onChangeFilter('orderType', e.target.value)}
                                            >
                                                <MenuItem value="all">All</MenuItem>
                                                <MenuItem value="dinein">Dine In</MenuItem>
                                                <MenuItem value="takeaway">Takeaway</MenuItem>
                                                <MenuItem value="dispatch">Dispatch</MenuItem>
                                                <MenuItem value="delivery">Delivery</MenuItem>
                                                <MenuItem value="foodpanda">Foodpanda</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>User</InputLabel>
                                            <Select
                                                label="User"
                                                value={filters.selectedUserID}
                                                onChange={(e) => onChangeFilter('selectedUserID', e.target.value)}
                                            >
                                                <MenuItem value="all">All Users</MenuItem>
                                                {(userOptions || []).map((option) => (
                                                    <MenuItem key={option.value} value={String(option.value)}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Status</InputLabel>
                                            <Select
                                                label="Status"
                                                value={filters.orderStatus}
                                                onChange={(e) => onChangeFilter('orderStatus', e.target.value)}
                                            >
                                                <MenuItem value="all">All (Except Cancelled)</MenuItem>
                                                <MenuItem value="completed">Completed</MenuItem>
                                                <MenuItem value="active">Active / Held</MenuItem>
                                                <MenuItem value="draft">Draft</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Payment Method</InputLabel>
                                            <Select
                                                label="Payment Method"
                                                value={filters.paymentMethod}
                                                onChange={(e) => onChangeFilter('paymentMethod', e.target.value)}
                                            >
                                                <MenuItem value="all">All Methods</MenuItem>
                                                {paymentMethodOptions.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 3 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Search"
                                            placeholder="Order, KOT, customer, phone, table"
                                            value={filters.search}
                                            onChange={(e) => onChangeFilter('search', e.target.value)}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 3 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Item Wise Search"
                                            placeholder="Item name or item code"
                                            value={filters.itemSearch}
                                            onChange={(e) => onChangeFilter('itemSearch', e.target.value)}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 1.5 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Min Amount"
                                            value={filters.minAmount}
                                            onChange={(e) => onChangeFilter('minAmount', e.target.value)}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 1.5 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Max Amount"
                                            value={filters.maxAmount}
                                            onChange={(e) => onChangeFilter('maxAmount', e.target.value)}
                                        />
                                    </Grid2>
                                </Grid2>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Paper sx={{ mt: 2 }}>
                        <Tabs
                            value={activeReportTab}
                            onChange={(_event, value) => setActiveReportTab(value)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ borderBottom: 1, borderColor: 'divider', px: 1, width: '100%' }}
                        >
                            <Tab value="summary" label="Summary" />
                            <Tab value="detail" label="Detail Report" />
                            <Tab value="unallocated" label={"Unallocated Orders (" + (detailReport?.unallocatedOrders?.length || 0) + ")"} />
                            <Tab value="discounts" label="Discounts" />
                            <Tab value="employee" label="Employee Sales" />
                            <Tab value="orderTaker" label="Order Taker Sales" />
                            <Tab value="category" label="Category Wise" />
                            <Tab value="item" label="Item Wise" />
                            <Tab value="deal" label="Deal Wise" />
                            <Tab value="orders" label="Order Wise" />
                        </Tabs>
                    </Paper>

                    {activeReportTab === 'detail' && <DetailReport detailReport={detailReport} amount={amount} loading={loading} error={error} />}
                    {activeReportTab === 'unallocated' && <UnallocatedOrdersReport orders={detailReport?.unallocatedOrders} amount={amount} loading={loading} error={error} />}
                    {activeReportTab === 'discounts' && <DiscountsReport rows={reportRows} amount={amount} loading={loading} error={error} />}
                    {activeReportTab === 'employee' && <EmployeeSalesReport employeeSales={detailReport?.employeeSales} amount={amount} loading={loading} error={error} />}
                    {activeReportTab === 'orderTaker' && <OrderTakerSalesReport orderTakerSales={detailReport?.orderTakerSales} amount={amount} loading={loading} error={error} />}

                    {activeReportTab === 'summary' && (
                        <Box sx={{ mt: 2 }}>
                            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                            {loading && (
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                    <CircularProgress size={18} />
                                    <Typography variant="body2">Loading report...</Typography>
                                </Stack>
                            )}
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                                    <Card><CardContent><Typography variant="caption">Total Orders</Typography><Typography variant="h6">{summary.totalOrders || 0}</Typography></CardContent></Card>
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                                    <Card><CardContent><Typography variant="caption">Total Amount</Typography><Typography variant="h6">{amount(summary.totalAmount)}</Typography></CardContent></Card>
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                                    <Card><CardContent><Typography variant="caption">Completed</Typography><Typography variant="h6">{summary.completedOrders || 0}</Typography></CardContent></Card>
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                                    <Card><CardContent><Typography variant="caption">Active/Held</Typography><Typography variant="h6">{summary.activeOrders || 0}</Typography></CardContent></Card>
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                                    <Card><CardContent><Typography variant="caption">Average Ticket</Typography><Typography variant="h6">{amount(summary.averageOrderAmount)}</Typography></CardContent></Card>
                                </Grid2>
                            </Grid2>

                            <Paper sx={{ mt: 2 }}>
                                <Box sx={{ p: 1.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Sales By Type</Typography>
                                    <Divider sx={{ my: 1.5 }} />
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Type</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow><TableCell>Dine In</TableCell><TableCell align="right">{amount(summary.dineInAmount)}</TableCell></TableRow>
                                                <TableRow><TableCell>Takeaway</TableCell><TableCell align="right">{amount(summary.takeawayAmount)}</TableCell></TableRow>
                                                <TableRow><TableCell>Dispatch</TableCell><TableCell align="right">{amount(summary.dispatchAmount)}</TableCell></TableRow>
                                                <TableRow><TableCell>Delivery</TableCell><TableCell align="right">{amount(summary.deliveryAmount)}</TableCell></TableRow>
                                                <TableRow><TableCell>Foodpanda</TableCell><TableCell align="right">{amount(summary.foodpandaAmount)}</TableCell></TableRow>
                                                <TableRow sx={{ bgcolor: 'warning.50' }}><TableCell sx={{ fontWeight: 700 }}>Employee Sales</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>{amount(summary.employeeAmount)}</TableCell></TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Paper>
                        </Box>
                    )}

                    {activeReportTab === 'category' && (
                        <Paper sx={{ mt: 2 }}>
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Category Wise Sales</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Category total ke sath us category ke items bhi yahin show honge.
                                </Typography>
                                <Divider sx={{ my: 1.5 }} />
                                {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                                {loading && (
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                        <CircularProgress size={18} />
                                        <Typography variant="body2">Loading report...</Typography>
                                    </Stack>
                                )}
                                <TableContainer sx={{ maxHeight: '65vh' }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Category / Item</TableCell>
                                                <TableCell align="right">Qty</TableCell>
                                                <TableCell align="right">Amount</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {categoryWiseSalesSummary.length > 0 ? (
                                                categoryWiseSalesSummary.map((category) => (
                                                    <React.Fragment key={`category-${category.categoryCode || category.categoryName}`}>
                                                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{category.categoryName}</Typography>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                                {category.totalQty.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                                {amount(category.totalAmount)}
                                                            </TableCell>
                                                        </TableRow>
                                                        {(category.items || []).map((item) => (
                                                            <TableRow key={`category-${category.categoryCode || category.categoryName}-${item.itemCode || item.itemName}`}>
                                                                <TableCell sx={{ pl: 4 }}>
                                                                    <Typography variant="body2">{item.itemName}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">Unit: {item.reportingUnitName}</Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {item.totalQty.toLocaleString('en-US', { maximumFractionDigits: 2 })} {item.reportingUnitName}
                                                                </TableCell>
                                                                <TableCell align="right">{amount(item.totalAmount)}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </React.Fragment>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={3} align="center">
                                                        Selected filters ke liye category wise sales data available nahi hai.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Paper>
                    )}

                    {activeReportTab === 'item' && (
                        <>
                            <Paper sx={{ mt: 2 }}>
                                <Box sx={{ p: 1.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Item Wise Total Sales</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Har item ki sirf final total sold quantity.
                                    </Typography>
                                    <Divider sx={{ my: 1.5 }} />
                                    {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                                    {loading && (
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                            <CircularProgress size={18} />
                                            <Typography variant="body2">Loading report...</Typography>
                                        </Stack>
                                    )}
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Item</TableCell>
                                                    <TableCell align="right">Total Sale</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {itemWiseTotalSalesSummary.length > 0 ? (
                                                    itemWiseTotalSalesSummary.map((item) => (
                                                        <TableRow key={`total-sales-${item.itemCode || item.itemName}`}>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.itemName}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Unit: {item.reportingUnitName}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {item.totalQty.toLocaleString('en-US', { maximumFractionDigits: 2 })} {item.reportingUnitName}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={2} align="center">
                                                            Selected filters ke liye item wise total sales data available nahi hai.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Paper>

                            <Paper sx={{ mt: 2 }}>
                                <Box sx={{ p: 1.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Item Consumption Summary</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Separate sale aur deals dono ko base unit aur equivalent plate level par break down kiya gaya hai.
                                    </Typography>
                                    <Divider sx={{ my: 1.5 }} />
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Item</TableCell>
                                                    <TableCell align="right">Separate</TableCell>
                                                    <TableCell align="right">Deals</TableCell>
                                                    <TableCell align="right">Deal Plate Eqv.</TableCell>
                                                    <TableCell align="right">Total Base</TableCell>
                                                    <TableCell align="right">Total Plate Eqv.</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(itemConsumptionSummary || []).length > 0 ? (
                                                    itemConsumptionSummary.map((item) => (
                                                        <TableRow key={`${item.itemCode || item.itemName}`}>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.itemName}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {item.baseUnitsPerReportingUnit} {item.baseUnitName} = 1 {item.reportingUnitName}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {Number(item.separateSoldQty || 0).toLocaleString('en-US')} {item.reportingUnitName}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {Number(item.dealBaseUnitQty || 0).toLocaleString('en-US')} {item.baseUnitName}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {Number(item.dealEquivalentReportingQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {Number(item.totalBaseUnitQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })} {item.baseUnitName}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {Number(item.totalEquivalentReportingQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })} {item.reportingUnitName}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center">
                                                            Selected filters ke liye item consumption data available nahi hai.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Paper>
                        </>
                    )}

                    {activeReportTab === 'deal' && (
                        <Box sx={{ mt: 2 }}>
                            <Card sx={{ mb: 2, bgcolor: 'primary.50' }}>
                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Typography variant="caption" color="text.secondary">Deals Sales Grand Total</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{amount(dealSalesOverview.totalAmount)}</Typography>
                                    <Typography variant="body2" color="text.secondary">{dealSalesOverview.totalOrders.toLocaleString()} deal order(s)</Typography>
                                </CardContent>
                            </Card>
                            <Grid2 container spacing={1.5}>
                                {dealSalesOverview.rows.map((category) => (
                                    <Grid2 key={category.label} size={{ xs: 12, sm: 6, md: 3 }}>
                                        <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                                            <Chip size="small" label={category.label} color={category.label === 'Foodpanda' ? 'warning' : 'primary'} variant="outlined" sx={{ mb: 1 }} />
                                            <Typography variant="body2">Orders: <b>{category.orderCount.toLocaleString()}</b></Typography>
                                            <Typography variant="body2">Amount: <b>{amount(category.amount)}</b></Typography>
                                        </Paper>
                                    </Grid2>
                                ))}
                            </Grid2>
                        </Box>
                    )}

                    {activeReportTab === 'deal' && (
                        <Paper sx={{ mt: 2 }}>
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Deal Wise Sales</Typography>
                                <Typography variant="body2" color="text.secondary">Selected filters ke mutabiq har deal ki total quantity aur kitne orders mein gayi hai.</Typography>
                                <Divider sx={{ my: 1.5 }} />
                                {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                                {loading && <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}><CircularProgress size={18} /><Typography variant="body2">Loading report...</Typography></Stack>}
                                <TableContainer sx={{ maxHeight: '65vh' }}><Table stickyHeader size="small">
                                    <TableHead><TableRow><TableCell>Deal</TableCell><TableCell align="right">Total Qty</TableCell><TableCell align="right">Orders</TableCell><TableCell>Order Nos.</TableCell></TableRow></TableHead>
                                    <TableBody>{dealWiseSalesSummary.length > 0 ? dealWiseSalesSummary.map((deal) => (
                                        <TableRow key={deal.dealID || deal.dealName} hover>
                                            <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{deal.dealName || 'Deal'}</Typography>{deal.dealID && <Typography variant="caption" color="text.secondary">Deal ID: {deal.dealID}</Typography>}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>{Number(deal.totalQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="right">{Number(deal.orderCount || 0).toLocaleString()}</TableCell>
                                            <TableCell>{(deal.orderIDs || []).map((orderID) => `#${orderID}`).join(', ')}</TableCell>
                                        </TableRow>
                                    )) : <TableRow><TableCell colSpan={4} align="center">Selected filters ke liye koi deal sale nahi mili.</TableCell></TableRow>}</TableBody>
                                </Table></TableContainer>
                            </Box>
                        </Paper>
                    )}

                    {activeReportTab === 'deal' && (
                        <Paper sx={{ mt: 2 }}>
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Deal Detail by Order Type</Typography>
                                <Typography variant="body2" color="text.secondary">Har deal ki order type-wise quantity, order count aur sale amount.</Typography>
                                <Divider sx={{ my: 1.5 }} />
                                <TableContainer sx={{ maxHeight: '65vh' }}><Table stickyHeader size="small">
                                    <TableHead><TableRow><TableCell>Deal</TableCell><TableCell>Order Type</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Orders</TableCell><TableCell align="right">Amount</TableCell><TableCell>Order Nos.</TableCell></TableRow></TableHead>
                                    <TableBody>{dealWiseDetailGroups.length > 0 ? dealWiseDetailGroups.map((group) => (
                                        <React.Fragment key={group.dealID || group.dealName}>
                                            {group.rows.map((deal) => (
                                                <TableRow key={`${group.dealID || group.dealName}:${deal.orderType}`} hover>
                                                    <TableCell sx={{ fontWeight: 700 }}>{deal.dealName || 'Deal'}</TableCell>
                                                    <TableCell><Chip size="small" label={deal.orderType} variant="outlined" /></TableCell>
                                                    <TableCell align="right">{Number(deal.totalQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}</TableCell>
                                                    <TableCell align="right">{Number(deal.orderCount || 0).toLocaleString()}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{amount(deal.totalAmount)}</TableCell>
                                                    <TableCell>{(deal.orderIDs || []).map((orderID) => `#${orderID}`).join(', ')}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{ bgcolor: 'primary.50' }}>
                                                <TableCell sx={{ fontWeight: 800 }}>{group.dealName || 'Deal'} Total</TableCell>
                                                <TableCell><Chip size="small" label="Total" color="primary" /></TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 800 }}>{Number(group.totalQty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 800 }}>{Number(group.orderCount || 0).toLocaleString()}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 800 }}>{amount(group.totalAmount)}</TableCell>
                                                <TableCell>-</TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    )) : <TableRow><TableCell colSpan={6} align="center">Selected filters ke liye koi deal detail available nahi hai.</TableCell></TableRow>}</TableBody>
                                </Table></TableContainer>
                            </Box>
                        </Paper>
                    )}

                    {activeReportTab === 'orders' && (
                        <Paper sx={{ mt: 2 }}>
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Order-Level Sales Report</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Same operational data as POS reprint flow, presented in report format.
                                </Typography>
                                <Divider sx={{ my: 1.5 }} />

                                {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                                {loading && (
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                        <CircularProgress size={18} />
                                        <Typography variant="body2">Loading report...</Typography>
                                    </Stack>
                                )}

                                <TableContainer sx={{ maxHeight: '65vh' }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Action</TableCell>
                                                <TableCell>Order</TableCell>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>KOT / Ticket</TableCell>
                                                <TableCell>Customer</TableCell>
                                                <TableCell>Payment</TableCell>
                                                <TableCell align="right">Amount</TableCell>
                                                <TableCell>Print</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {visibleRows.map((row) => {
                                                const isFoodpanda = String(row.aggregatorPlatform || '').toLowerCase() === 'foodpanda';
                                                const isOpen = !!expandedOrders[row.orderID];
                                                return (
                                                    <React.Fragment key={row.orderID}>
                                                        <TableRow hover>
                                                            <TableCell>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={() => handleToggleOrder(row.orderID)}
                                                                >
                                                                    {isOpen ? 'Hide Order' : 'Show Order'}
                                                                </Button>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>#{row.orderID}</Typography>
                                                                {row.tableLabel && (
                                                                    <Typography variant="caption" color="text.secondary">Table: {row.tableLabel}</Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">{row.entDate ? dayjs(row.entDate).format('DD-MMM-YYYY') : '-'}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {row.createdAt ? dayjs(row.createdAt).format('hh:mm A') : '-'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    label={isFoodpanda ? 'Foodpanda' : String(row.orderType || '').toUpperCase()}
                                                                    color={isFoodpanda ? 'warning' : 'primary'}
                                                                    variant={isFoodpanda ? 'filled' : 'outlined'}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    label={String(row.orderStatus || 'N/A').toUpperCase()}
                                                                    color={String(row.orderStatus || '').toLowerCase() === 'completed' ? 'success' : 'default'}
                                                                    variant="outlined"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">KOT: {row.kotNumber || 'N/A'}</Typography>
                                                                <Typography variant="caption" color="text.secondary">Ticket: {row.ticketNo || 'N/A'}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">{row.customerName || '-'}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{row.customerPhone || '-'}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">{row.paymentMethods || 'N/A'}</Typography>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                                {amount(row.grandTotal)}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {row.lastBillPrintedAt
                                                                        ? dayjs(row.lastBillPrintedAt).format('DD-MMM hh:mm A')
                                                                        : 'Not printed'}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell colSpan={10} sx={{ py: 0 }}>
                                                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                                    <Box sx={{ p: 1.5, bgcolor: '#fafafa', borderRadius: 1, m: 1 }}>
                                                                        <Grid2 container spacing={1}>
                                                                            <Grid2 size={{ xs: 12, md: 3 }}>
                                                                                <Typography variant="caption" color="text.secondary">Created At</Typography>
                                                                                <Typography variant="body2">{row.createdAt ? dayjs(row.createdAt).format('DD-MMM-YYYY hh:mm:ss A') : '-'}</Typography>
                                                                            </Grid2>
                                                                            <Grid2 size={{ xs: 12, md: 3 }}>
                                                                                <Typography variant="caption" color="text.secondary">Completed At</Typography>
                                                                                <Typography variant="body2">{row.completedAt ? dayjs(row.completedAt).format('DD-MMM-YYYY hh:mm:ss A') : '-'}</Typography>
                                                                            </Grid2>
                                                                            <Grid2 size={{ xs: 12, md: 3 }}>
                                                                                <Typography variant="caption" color="text.secondary">Aggregator</Typography>
                                                                                <Typography variant="body2">{row.aggregatorPlatform || '-'}</Typography>
                                                                            </Grid2>
                                                                            <Grid2 size={{ xs: 12, md: 3 }}>
                                                                                <Typography variant="caption" color="text.secondary">Aggregator Order #</Typography>
                                                                                <Typography variant="body2">{row.aggregatorOrderCode || '-'}</Typography>
                                                                            </Grid2>
                                                                        </Grid2>

                                                                        <Box sx={{ mt: 1.5 }}>
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                                                Items Detail
                                                                            </Typography>
                                                                            <Table size="small">
                                                                                <TableHead>
                                                                                    <TableRow>
                                                                                        <TableCell>Item Code</TableCell>
                                                                                        <TableCell>Item Name</TableCell>
                                                                                        <TableCell align="right">Qty</TableCell>
                                                                                        <TableCell align="right">Rate</TableCell>
                                                                                        <TableCell align="right">Discount</TableCell>
                                                                                        <TableCell align="right">Amount</TableCell>
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {(row.items || []).length > 0 ? (
                                                                                        (row.items || []).map((item, itemIndex) => (
                                                                                            <TableRow key={`${row.orderID}-${item.orderItemID || itemIndex}`}>
                                                                                                <TableCell>{item.itemCode || '-'}</TableCell>
                                                                                                <TableCell>{item.itemName || '-'}</TableCell>
                                                                                                <TableCell align="right">{Number(item.quantity || 0).toLocaleString('en-US')}</TableCell>
                                                                                                <TableCell align="right">{Number(item.unitPrice || 0).toLocaleString('en-US')}</TableCell>
                                                                                                <TableCell align="right">{Number(item.lineDiscount || 0).toLocaleString('en-US')}</TableCell>
                                                                                                <TableCell align="right">{Number(item.itemTotal || 0).toLocaleString('en-US')}</TableCell>
                                                                                            </TableRow>
                                                                                        ))
                                                                                    ) : (
                                                                                        <TableRow>
                                                                                            <TableCell colSpan={6} align="center">
                                                                                                No item details found for this order.
                                                                                            </TableCell>
                                                                                        </TableRow>
                                                                                    )}
                                                                                </TableBody>
                                                                            </Table>
                                                                        </Box>
                                                                    </Box>
                                                                </Collapse>
                                                            </TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                );
                                            })}

                                            {!loading && visibleRows.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={10} align="center">
                                                        No records found for selected filters.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    count={reportRows.length}
                                    page={page}
                                    onPageChange={(_event, newPage) => setPage(newPage)}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(event) => {
                                        setRowsPerPage(parseInt(event.target.value, 10));
                                        setPage(0);
                                    }}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                />
                            </Box>
                        </Paper>
                    )}
                </Box>
            </Container>
            <Dialog
                open={printPreviewOpen}
                onClose={handleClosePrintPreview}
                maxWidth={false}
                fullWidth
                PaperProps={{ sx: { width: 'min(1200px, 98vw)' } }}
            >
                <DialogTitle>Thermal Print Preview</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        80mm thermal layout preview. Selected tab ka print ya PDF yahin se nikal sakte hain.
                    </Typography>
                    <Paper variant="outlined" sx={{ mb: 1.5 }}>
                        <Tabs
                            value={thermalReportTab}
                            onChange={(_event, value) => setThermalReportTab(value)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab value="summary" label="Summary" />
                            <Tab value="detail" label="Detail Report" />
                            <Tab value="discounts" label="Discounts" />
                            <Tab value="employee" label="Employee Sales" />
                            <Tab value="orderTaker" label="Order Taker Sales" />
                            <Tab value="category" label="Category Wise" />
                            <Tab value="item" label="Item Wise" />
                            <Tab value="orders" label="Order Wise" />
                            <Tab value="ordersWithItems" label="Orders With Items" />
                        </Tabs>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                        <Box
                            component="div"
                            sx={{
                                my: 0,
                                mx: 'auto',
                                width: '72mm',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                fontFamily: '"Times New Roman", Times, serif',
                                fontSize: 15,
                                lineHeight: 1.4
                            }}
                        >
                            {thermalPreviewText.split('\n').map((line, index) => {
                                const layout = parseThermalLine(line);
                                if (layout.type === 'divider') {
                                    return <Box key={index} sx={{ borderTop: '1px solid #111', my: 0.6 }} />;
                                }
                                if (layout.type === 'blank') {
                                    return <Box key={index} sx={{ minHeight: '0.7em' }} />;
                                }
                                if (layout.type === 'row') {
                                    return (
                                        <Stack key={index} direction="row" justifyContent="space-between" spacing={2} sx={{ width: '100%' }}>
                                            <Box component="span" sx={{ textAlign: 'left' }}>{layout.left}</Box>
                                            <Box component="span" sx={{ textAlign: 'right', ml: 'auto', whiteSpace: 'nowrap' }}>{layout.right}</Box>
                                        </Stack>
                                    );
                                }
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            minHeight: '1.4em',
                                            width: '100%',
                                            textAlign: layout.type === 'heading' ? 'center' : 'left',
                                            fontWeight: layout.type === 'heading' ? 800 : 400,
                                            fontSize: layout.type === 'heading' ? 16 : 'inherit'
                                        }}
                                    >
                                        {layout.text}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePrintPreview}>Close</Button>
                    <Button variant="outlined" startIcon={<DownloadForOfflineIcon />} onClick={handleDownloadThermalPdf}>
                        Download PDF
                    </Button>
                    <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrintThermal}>
                        Print 80mm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SalesReport;
